// ============================================================================
// Mock Data v2 for 008-measurement-philosophy-rework Prototype
// Iteration 2: Full freedom diagnosis, verdict builder, narrative dashboard
// ============================================================================

// ==========================================================================
// DIAGNOSIS DIMENSIONS (now with full instructions, not just toggles)
// ==========================================================================

export interface DiagnosisDimension {
  dimensionId: string;
  name: string;
  description: string;
  instructions: string; // Full AI instructions (expandable in UI)
  outputFormat: string; // What the AI produces
  frameworks: string[];
  isSystem: boolean;
  enabled: boolean;
}

export const DIAGNOSIS_DIMENSION_LIBRARY: DiagnosisDimension[] = [
  {
    dimensionId: 'cepMentalAvailability',
    name: 'CEP & Mental Availability',
    description: 'Does the ad associate the brand with relevant buying situations?',
    instructions: 'Evaluate: Which category entry points (buying occasions, needs, motivations) does this ad link to the brand?\nAnalyze: How many distinct CEPs are activated? Are they high-penetration (common) or niche?\nOutput facts: List each CEP triggered, the visual/audio cue that triggers it, and its estimated penetration.\nInterpretation: Assess whether the ad builds broad mental availability or focuses narrowly.',
    outputFormat: '{ facts: ["CEP identified: morning routine (trigger: kitchen scene at 0:03)", ...], interpretation: "The ad activates 3 high-penetration CEPs..." }',
    frameworks: ['Ehrenberg-Bass', 'Romaniuk'],
    isSystem: false,
    enabled: true,
  },
  {
    dimensionId: 'brandLinkageDistinctiveness',
    name: 'Brand Linkage & Distinctiveness',
    description: 'Could only this brand have made this ad? The "logo removal test."',
    instructions: 'Evaluate: If the logo and brand name were removed, what percentage of category buyers could identify the brand?\nAnalyze: Which distinctive brand assets are deployed (colors, characters, sonic, tagline, pack shape)? How early and prominently?\nOutput facts: List each distinctive asset, its first appearance timestamp, and clarity.\nInterpretation: Rate distinctiveness as generic (<30%), partial (30-60%), distinctive (60-90%), or iconic (90%+).',
    outputFormat: '{ facts: ["Logo appears at 0:01, clear", "Brand color #FF0000 dominant in 80% of frames", ...], interpretation: "Partial distinctiveness (est. 45%) — relies heavily on logo..." }',
    frameworks: ['Ehrenberg-Bass'],
    isSystem: false,
    enabled: true,
  },
  {
    dimensionId: 'emotionalRegister',
    name: 'Emotional Register',
    description: 'System1 / Orlando Wood: brand-building vs activation style.',
    instructions: 'Evaluate: Does the ad use brand-building creative features (named character, incident/story, determinable place) or activation features (product focus, urgency, text overlays)?\nAnalyze: Apply Orlando Wood\'s System1 framework. Count brand-building vs activation features.\nOutput facts: List each feature identified with timestamp.\nInterpretation: Classify as brand_building (all 3 CIP present), hybrid (mixed), or activation (product/offer dominant).',
    outputFormat: '{ facts: ["Character: protagonist \'Sarah\' introduced at 0:02", "Incident: coffee spill sequence 0:05-0:08", ...], interpretation: "Brand-building register — all three CIP elements present..." }',
    frameworks: ['System1', 'Orlando Wood'],
    isSystem: false,
    enabled: true,
  },
  {
    dimensionId: 'fluencyComprehension',
    name: 'Fluency & Comprehension',
    description: 'Is the message decoded without cognitive effort?',
    instructions: 'Evaluate: Can a viewer understand the core message in a single viewing without pausing?\nAnalyze: Information density per scene, reading speed of text overlays, audio-visual congruence.\nOutput facts: Note any moments of cognitive overload, text too fast to read, or audio-visual mismatch.\nInterpretation: Rate processing fluency as high (effortless), medium (requires attention), or low (confusing).',
    outputFormat: '{ facts: ["Text overlay at 0:04 shows 12 words for 1.5s — too fast", ...], interpretation: "Medium fluency — core message clear but secondary text requires re-watching..." }',
    frameworks: [],
    isSystem: false,
    enabled: true,
  },
  {
    dimensionId: 'narrativeStructure',
    name: 'Narrative Structure',
    description: 'Pacing, arc, and scene sequencing effectiveness.',
    instructions: 'Evaluate: Does the ad follow an effective narrative arc (open loop, tension, resolution)?\nAnalyze: Scene count, average scene duration, pacing rhythm, presence of cliffhanger/payoff structure.\nOutput facts: Map the narrative arc beat by beat with timestamps.\nInterpretation: Assess whether the structure serves the objective (brand recall for awareness, action for DR).',
    outputFormat: '{ facts: ["Hook/open loop: 0:00-0:03 (question posed)", "Rising tension: 0:03-0:10", ...], interpretation: "Strong open-loop structure drives completion..." }',
    frameworks: [],
    isSystem: false,
    enabled: true,
  },
  {
    dimensionId: 'activationHook',
    name: 'Activation & Hook',
    description: 'First 3 seconds: scroll-stop factor and pattern interrupt.',
    instructions: 'Evaluate: Does the first 3 seconds create a pattern interrupt strong enough to stop scrolling?\nAnalyze: Visual contrast, motion energy, curiosity gap, relevance signal, audio hook.\nOutput facts: Describe exactly what happens in 0:00-0:03 and rate each scroll-stop element.\nInterpretation: Rate hook strength and predict 3-second view rate impact.',
    outputFormat: '{ facts: ["0:00: Bold text \'STOP\' with zoom effect", "0:01: Face close-up with surprised expression", ...], interpretation: "Strong pattern interrupt — visual contrast + emotion should drive above-average 3s views..." }',
    frameworks: ['Direct Response'],
    isSystem: false,
    enabled: true,
  },
  {
    dimensionId: 'offerValueProposition',
    name: 'Offer & Value Proposition',
    description: 'Is the offer specific, credible, and differentiated?',
    instructions: 'Evaluate: Is there a clear, specific offer? Is it credible? Is it differentiated from competitors?\nAnalyze: Price anchoring, urgency/scarcity mechanics, risk reversal (guarantees), social proof supporting the offer.\nOutput facts: Quote the exact offer as presented, note any urgency mechanics, and identify any missing elements.\nInterpretation: Rate offer clarity and predict conversion impact.',
    outputFormat: '{ facts: ["Offer: \'50% off first 3 months\' shown at 0:12", "Urgency: \'Limited time\' badge", "Missing: no risk reversal/guarantee", ...], interpretation: "Clear offer but lacks credibility signals..." }',
    frameworks: ['Direct Response'],
    isSystem: false,
    enabled: true,
  },
  {
    dimensionId: 'socialProofTrust',
    name: 'Social Proof & Trust',
    description: 'Testimonials, reviews, authority signals, credibility markers.',
    instructions: 'Evaluate: What trust-building elements are present? Testimonials, reviews, expert endorsements, user counts, certifications?\nAnalyze: Authenticity of social proof (real vs staged), specificity (named person vs anonymous), placement and prominence.\nOutput facts: List each social proof element with timestamp and authenticity assessment.\nInterpretation: Rate trust level and identify gaps.',
    outputFormat: '{ facts: ["Testimonial from \'Sarah M.\' at 0:08 — appears authentic (selfie-style)", "4.8 star rating shown at 0:15", ...], interpretation: "Strong social proof layer — specific, named, authentic feel..." }',
    frameworks: ['Direct Response'],
    isSystem: false,
    enabled: true,
  },
];

// ==========================================================================
// KPI OPTIONS (full list with categories)
// ==========================================================================

export interface KpiOption {
  id: string;
  label: string;
  shortLabel: string;
  unit: string;
  category: 'delivery' | 'traffic' | 'engagement' | 'video' | 'conversion' | 'derived';
  higherIsBetter: boolean;
  description: string;
  optimizationGoals: string[];
}

export const KPI_OPTIONS: KpiOption[] = [
  // Delivery
  { id: 'spend', label: 'Ad Spend', shortLabel: 'Spend', unit: '$', category: 'delivery', higherIsBetter: false, description: 'Total amount spent on the ad', optimizationGoals: [] },
  { id: 'impressions', label: 'Impressions', shortLabel: 'Impr.', unit: '', category: 'delivery', higherIsBetter: true, description: 'Number of times the ad was shown', optimizationGoals: [] },
  { id: 'reach', label: 'Reach', shortLabel: 'Reach', unit: '', category: 'delivery', higherIsBetter: true, description: 'Unique people who saw the ad', optimizationGoals: ['Reach', 'Brand Awareness'] },
  { id: 'cpm', label: 'CPM', shortLabel: 'CPM', unit: '$', category: 'delivery', higherIsBetter: false, description: 'Cost per 1,000 impressions', optimizationGoals: [] },
  { id: 'frequency', label: 'Frequency', shortLabel: 'Freq.', unit: 'x', category: 'delivery', higherIsBetter: false, description: 'Average times each person saw the ad', optimizationGoals: [] },

  // Traffic
  { id: 'clicks', label: 'Clicks', shortLabel: 'Clicks', unit: '', category: 'traffic', higherIsBetter: true, description: 'Total clicks on the ad', optimizationGoals: [] },
  { id: 'ctr', label: 'CTR (Click-Through Rate)', shortLabel: 'CTR', unit: '%', category: 'traffic', higherIsBetter: true, description: 'Clicks / impressions', optimizationGoals: ['Link Clicks', 'Traffic'] },
  { id: 'cpc', label: 'CPC (Cost per Click)', shortLabel: 'CPC', unit: '$', category: 'traffic', higherIsBetter: false, description: 'Average cost per click', optimizationGoals: [] },
  { id: 'linkCtr', label: 'Link CTR', shortLabel: 'Link CTR', unit: '%', category: 'traffic', higherIsBetter: true, description: 'Link clicks / impressions', optimizationGoals: [] },
  { id: 'landingPageViews', label: 'Landing Page Views', shortLabel: 'LPV', unit: '', category: 'traffic', higherIsBetter: true, description: 'People who loaded the landing page', optimizationGoals: [] },

  // Video
  { id: 'vtr', label: 'VTR (View-Through Rate)', shortLabel: 'VTR', unit: '%', category: 'video', higherIsBetter: true, description: 'ThruPlays / impressions', optimizationGoals: ['ThruPlay'] },
  { id: 'thruplays', label: 'ThruPlays', shortLabel: 'ThruPlays', unit: '', category: 'video', higherIsBetter: true, description: '15s+ views or complete views', optimizationGoals: ['ThruPlay'] },
  { id: 'costPerThruplay', label: 'Cost per ThruPlay', shortLabel: 'CPThru', unit: '$', category: 'video', higherIsBetter: false, description: 'Spend / thruplays', optimizationGoals: [] },
  { id: 'avgWatchTime', label: 'Avg Watch Time', shortLabel: 'Avg Watch', unit: 's', category: 'video', higherIsBetter: true, description: 'Average seconds watched', optimizationGoals: [] },

  // Derived
  { id: 'hookRate', label: 'Hook Rate', shortLabel: 'Hook Rate', unit: '%', category: 'derived', higherIsBetter: true, description: '3-second views / impressions — measures scroll-stopping power', optimizationGoals: [] },
  { id: 'holdRate', label: 'Hold Rate', shortLabel: 'Hold Rate', unit: '%', category: 'derived', higherIsBetter: true, description: 'ThruPlays / 3-second views — measures retention after hook', optimizationGoals: [] },

  // Conversion
  { id: 'roas', label: 'ROAS', shortLabel: 'ROAS', unit: 'x', category: 'conversion', higherIsBetter: true, description: 'Purchase value / spend', optimizationGoals: ['Purchases', 'Conversions'] },
  { id: 'cpa', label: 'CPA', shortLabel: 'CPA', unit: '$', category: 'conversion', higherIsBetter: false, description: 'Cost per acquisition/purchase', optimizationGoals: ['Conversions', 'Leads'] },
  { id: 'purchases', label: 'Purchases', shortLabel: 'Purchases', unit: '', category: 'conversion', higherIsBetter: true, description: 'Total purchases attributed', optimizationGoals: ['Purchases'] },
  { id: 'purchaseValue', label: 'Purchase Value', shortLabel: 'Revenue', unit: '$', category: 'conversion', higherIsBetter: true, description: 'Total revenue from purchases', optimizationGoals: [] },
];

// ==========================================================================
// GATE DEFINITIONS (now with full instructions)
// ==========================================================================

export interface MockGate {
  gateId: string;
  name: string;
  role: 'critical' | 'standard';
  isSystem: boolean;
  instructions: string;
}

const SYSTEM_GATES: MockGate[] = [
  {
    gateId: 'gate_productionErrors',
    name: 'Production Quality',
    role: 'critical',
    isSystem: true,
    instructions: 'Evaluate: Does the video have any technical production issues?\nPASS when: No visible glitches, artifacts, frame drops, audio sync issues, or resolution problems.\nFAIL when: Any noticeable production error that a viewer would perceive as unprofessional.',
  },
  {
    gateId: 'gate_formatHygiene',
    name: 'Format Hygiene',
    role: 'critical',
    isSystem: true,
    instructions: 'Evaluate: Does the creative meet platform format requirements?\nPASS when: Correct aspect ratio for placement, safe zones respected, text within readable limits.\nFAIL when: Wrong aspect ratio, key content cut off, text too small or outside safe zones.',
  },
];

// ==========================================================================
// STARTER TEMPLATES (v2: full instructions on everything)
// ==========================================================================

export interface StarterTemplate {
  key: string;
  name: string;
  philosophy: string;
  description: string;
  gates: MockGate[];
  diagnosisDimensions: DiagnosisDimension[];
  performanceKpi: {
    mode: 'auto' | 'manual';
    primaryKpiId: string | null;
    secondaryKpiIds: string[];
    benchmark: number | null;
  } | null;
}

export const STARTER_TEMPLATES: StarterTemplate[] = [
  {
    key: 'brandBuilder',
    name: 'Brand Builder',
    philosophy: 'Ehrenberg-Bass mental availability framework',
    description: 'For brand-building campaigns. Evaluates distinctive assets, mental availability, and brand linkage alongside VTR as the primary performance metric.',
    gates: [
      ...SYSTEM_GATES,
      { gateId: 'gate_brandTiming', name: 'Brand Timing (2s)', role: 'critical', isSystem: false, instructions: 'Evaluate: Does the brand appear within the first 2 seconds?\nPASS when: Logo, brand name, or distinctive brand asset is clearly visible before the 2-second mark.\nFAIL when: First brand cue appears after 2 seconds.' },
      { gateId: 'gate_clearClaim', name: 'Clear Claim', role: 'standard', isSystem: false, instructions: 'Evaluate: Is there a clear, singular message the viewer takes away?\nPASS when: One primary claim is communicated and reinforced.\nFAIL when: Message is muddled, multiple competing claims, or no clear takeaway.' },
      { gateId: 'gate_productShown', name: 'Product Shown', role: 'standard', isSystem: false, instructions: 'Evaluate: Is the product clearly shown in use or context?\nPASS when: Product is visible for 2+ seconds in a relevant context.\nFAIL when: Product is absent, barely visible, or shown without context.' },
      { gateId: 'gate_ctaPresent', name: 'CTA Present', role: 'standard', isSystem: false, instructions: 'Evaluate: Is there a call-to-action?\nPASS when: A clear CTA is present (verbal, text overlay, or visual cue).\nFAIL when: No discernible call-to-action.' },
      { gateId: 'gate_brandColorsUsed', name: 'Brand Colors', role: 'standard', isSystem: false, instructions: 'Evaluate: Are brand colors used as a distinctive asset?\nPASS when: Brand palette is prominently and consistently used.\nFAIL when: Brand colors are absent or inconsistently applied.' },
    ],
    diagnosisDimensions: DIAGNOSIS_DIMENSION_LIBRARY.filter(d =>
      ['cepMentalAvailability', 'brandLinkageDistinctiveness', 'emotionalRegister', 'fluencyComprehension', 'narrativeStructure'].includes(d.dimensionId)
    ).map(d => ({ ...d, enabled: true })),
    performanceKpi: { mode: 'auto', primaryKpiId: null, secondaryKpiIds: [], benchmark: null },
  },
  {
    key: 'directResponse',
    name: 'Direct Response',
    philosophy: 'Activation & conversion-focused evaluation',
    description: 'For performance campaigns. Evaluates hook strength, offer clarity, and CTA prominence alongside ROAS as the primary performance metric.',
    gates: [
      ...SYSTEM_GATES,
      { gateId: 'gate_hookStrength', name: 'Hook Strength', role: 'critical', isSystem: false, instructions: 'Evaluate: Do the first 3 seconds create a strong pattern interrupt?\nPASS when: Clear scroll-stopping element (visual contrast, curiosity gap, emotional trigger, or motion energy) is present in first 3s.\nFAIL when: Opening is generic, slow, or lacks any attention-grabbing element.' },
      { gateId: 'gate_offerClarity', name: 'Offer Clarity', role: 'critical', isSystem: false, instructions: 'Evaluate: Is the offer/value proposition clearly communicated?\nPASS when: Specific offer, benefit, or value proposition is stated clearly with supporting proof.\nFAIL when: Offer is vague, buried, or missing entirely.' },
      { gateId: 'gate_ctaProminence', name: 'CTA Prominence', role: 'critical', isSystem: false, instructions: 'Evaluate: Is the call-to-action prominent and actionable?\nPASS when: CTA is visually prominent, specific ("Shop Now", "Get 50% Off"), and appears at the right moment.\nFAIL when: CTA is weak ("Learn More"), hidden, or poorly timed.' },
      { gateId: 'gate_urgency', name: 'Urgency / Scarcity', role: 'standard', isSystem: false, instructions: 'Evaluate: Is there a reason to act now?\nPASS when: Time limit, quantity limit, or consequence of inaction is communicated.\nFAIL when: No urgency or scarcity mechanism present.' },
    ],
    diagnosisDimensions: DIAGNOSIS_DIMENSION_LIBRARY.filter(d =>
      ['activationHook', 'offerValueProposition', 'socialProofTrust', 'fluencyComprehension'].includes(d.dimensionId)
    ).map(d => ({ ...d, enabled: true })),
    performanceKpi: { mode: 'auto', primaryKpiId: null, secondaryKpiIds: [], benchmark: null },
  },
  {
    key: 'productionQA',
    name: 'Production QA',
    philosophy: 'Pre-flight technical check',
    description: 'For pre-launch QA. Checks production quality and format compliance only. No diagnosis. No performance KPIs.',
    gates: [
      ...SYSTEM_GATES,
      { gateId: 'gate_audioQuality', name: 'Audio Quality', role: 'critical', isSystem: false, instructions: 'Evaluate: Is the audio production professional and clear?\nPASS when: Clean audio, proper levels, no clipping, music/VO balanced.\nFAIL when: Audio clipping, level imbalance, background noise, or sync issues.' },
      { gateId: 'gate_visualPolish', name: 'Visual Polish', role: 'critical', isSystem: false, instructions: 'Evaluate: Is the visual production polished and intentional?\nPASS when: Consistent color grading, stable footage, clean transitions, readable text.\nFAIL when: Shaky footage, inconsistent grading, jarring transitions, or illegible text.' },
    ],
    diagnosisDimensions: [],
    performanceKpi: null,
  },
  {
    key: 'custom',
    name: 'Custom',
    philosophy: 'Build your own measurement framework',
    description: 'Start with system gates only. Add your own quality gates, diagnosis dimensions, and performance KPIs.',
    gates: [...SYSTEM_GATES],
    diagnosisDimensions: [],
    performanceKpi: { mode: 'auto', primaryKpiId: null, secondaryKpiIds: [], benchmark: null },
  },
];

// ==========================================================================
// DASHBOARD v2: NARRATIVE DATA
// ==========================================================================

export interface DashboardCreative {
  id: string;
  name: string;
  verdict: 'Scale' | 'Fix' | 'Kill';
  qualityScore: number; // gate pass rate %
  performanceScore: number | null; // KPI percentile
  primaryKpiLabel: string | null;
  primaryKpiValue: string | null;
  spend: number | null;
  gatesPassed: number;
  gatesTotal: number;
  editCount: number;
  taxonomyTags: Record<string, string>; // fieldName → optionLabel
}

export interface TaxonomyInsight {
  field: string;
  option: string;
  creativeCount: number;
  avgKpiValue: number;
  portfolioAvgKpiValue: number;
  multiplier: number; // e.g., 2.3 means 2.3x higher than average
  kpiLabel: string;
}

export interface WeeklyDelta {
  metric: string;
  label: string;
  currentValue: string;
  previousValue: string;
  delta: string; // e.g., "+3pp" or "-12%"
  direction: 'up' | 'down' | 'flat';
  isPositive: boolean; // up can be negative for cost metrics
}

export interface GateFailure {
  gateName: string;
  failCount: number;
  failRate: number;
  role: 'critical' | 'standard';
  topReason: string;
}

export interface Recommendation {
  type: 'quickWin' | 'kill' | 'pattern';
  title: string;
  description: string;
  creativeIds: string[];
  impact: string;
}

export interface NarrativeDashboardData {
  templateName: string;
  templateKey: string;
  mode: 'pre-flight' | 'in-market';
  totalCreatives: number;
  analyzedCreatives: number;
  withMetaData: number;

  // Portfolio health
  primaryKpi: { id: string; label: string; value: number; unit: string } | null;
  secondaryKpis: { id: string; label: string; value: number; unit: string }[];
  verdictDistribution: { scale: number; fix: number; kill: number };
  weeklyDeltas: WeeklyDelta[];

  // What's working
  topPerformers: DashboardCreative[];
  taxonomyInsights: TaxonomyInsight[];
  allCreatives: DashboardCreative[]; // for quadrant

  // What needs attention
  gateFailures: GateFailure[];
  luckyHits: DashboardCreative[]; // high perf, low quality
  underperformers: DashboardCreative[]; // low perf, high quality

  // What to do
  recommendations: Recommendation[];

  // Brand-specific metrics (Brand Builder only)
  brandMetrics?: {
    brandCueBy2sRate: number;
    avgLogoDuration: number;
    avgProductDuration: number;
    brandLinkageDistribution: { high: number; medium: number; low: number };
  };

  // Template-specific gate metrics
  qualityGates: { gateId: string; name: string; passRate: number; passCount: number; failCount: number; role: 'critical' | 'standard' }[];
}

// --- Brand Builder Dashboard Data ---

const BB_CREATIVES: DashboardCreative[] = [
  { id: 'bb1', name: 'Hero Summer 15s', verdict: 'Scale', qualityScore: 100, performanceScore: 82, primaryKpiLabel: 'VTR', primaryKpiValue: '38.2%', spend: 12500, gatesPassed: 7, gatesTotal: 7, editCount: 0, taxonomyTags: { hookType: 'Product Demo', format: 'Studio', claim: 'Quality' } },
  { id: 'bb2', name: 'Lifestyle Spring 30s', verdict: 'Scale', qualityScore: 100, performanceScore: 75, primaryKpiLabel: 'VTR', primaryKpiValue: '35.1%', spend: 8200, gatesPassed: 7, gatesTotal: 7, editCount: 0, taxonomyTags: { hookType: 'Lifestyle', format: 'Studio', claim: 'Emotional' } },
  { id: 'bb3', name: 'UGC Testimonial v2', verdict: 'Scale', qualityScore: 86, performanceScore: 88, primaryKpiLabel: 'VTR', primaryKpiValue: '41.5%', spend: 15300, gatesPassed: 6, gatesTotal: 7, editCount: 0, taxonomyTags: { hookType: 'Testimonial', format: 'UGC', claim: 'Social Proof' } },
  { id: 'bb4', name: 'Brand Film 60s', verdict: 'Fix', qualityScore: 71, performanceScore: 45, primaryKpiLabel: 'VTR', primaryKpiValue: '22.8%', spend: 6800, gatesPassed: 5, gatesTotal: 7, editCount: 2, taxonomyTags: { hookType: 'Story', format: 'Studio', claim: 'Emotional' } },
  { id: 'bb5', name: 'Product Close-up 6s', verdict: 'Fix', qualityScore: 57, performanceScore: 62, primaryKpiLabel: 'VTR', primaryKpiValue: '28.9%', spend: 4200, gatesPassed: 4, gatesTotal: 7, editCount: 3, taxonomyTags: { hookType: 'Product Demo', format: 'Studio', claim: 'Feature' } },
  { id: 'bb6', name: 'Influencer Collab', verdict: 'Scale', qualityScore: 100, performanceScore: 55, primaryKpiLabel: 'VTR', primaryKpiValue: '26.1%', spend: 9100, gatesPassed: 7, gatesTotal: 7, editCount: 0, taxonomyTags: { hookType: 'Testimonial', format: 'UGC', claim: 'Social Proof' } },
  { id: 'bb7', name: 'Seasonal Promo 15s', verdict: 'Kill', qualityScore: 29, performanceScore: 18, primaryKpiLabel: 'VTR', primaryKpiValue: '11.2%', spend: 3400, gatesPassed: 2, gatesTotal: 7, editCount: 0, taxonomyTags: { hookType: 'Offer', format: 'Motion Graphics', claim: 'Price' } },
  { id: 'bb8', name: 'Behind the Scenes', verdict: 'Fix', qualityScore: 71, performanceScore: 35, primaryKpiLabel: 'VTR', primaryKpiValue: '19.5%', spend: 2800, gatesPassed: 5, gatesTotal: 7, editCount: 1, taxonomyTags: { hookType: 'Story', format: 'UGC', claim: 'Authenticity' } },
  { id: 'bb9', name: 'Generic Banner v1', verdict: 'Kill', qualityScore: 14, performanceScore: 8, primaryKpiLabel: 'VTR', primaryKpiValue: '7.3%', spend: 1200, gatesPassed: 1, gatesTotal: 7, editCount: 0, taxonomyTags: { hookType: 'Static', format: 'Motion Graphics', claim: 'Feature' } },
  { id: 'bb10', name: 'Lucky UGC Reel', verdict: 'Kill', qualityScore: 29, performanceScore: 72, primaryKpiLabel: 'VTR', primaryKpiValue: '34.8%', spend: 7600, gatesPassed: 2, gatesTotal: 7, editCount: 0, taxonomyTags: { hookType: 'Testimonial', format: 'UGC', claim: 'Social Proof' } },
  { id: 'bb11', name: 'Concept Test A (Pre-flight)', verdict: 'Scale', qualityScore: 100, performanceScore: null, primaryKpiLabel: null, primaryKpiValue: null, spend: null, gatesPassed: 7, gatesTotal: 7, editCount: 0, taxonomyTags: { hookType: 'Product Demo', format: 'Studio', claim: 'Quality' } },
  { id: 'bb12', name: 'Concept Test B (Pre-flight)', verdict: 'Fix', qualityScore: 57, performanceScore: null, primaryKpiLabel: null, primaryKpiValue: null, spend: null, gatesPassed: 4, gatesTotal: 7, editCount: 2, taxonomyTags: { hookType: 'Story', format: 'Studio', claim: 'Emotional' } },
];

export const DASHBOARD_BRAND_BUILDER_V2: NarrativeDashboardData = {
  templateName: 'Brand Builder',
  templateKey: 'brandBuilder',
  mode: 'in-market',
  totalCreatives: 12,
  analyzedCreatives: 12,
  withMetaData: 10,

  primaryKpi: { id: 'vtr', label: 'VTR', value: 28.4, unit: '%' },
  secondaryKpis: [
    { id: 'reach', label: 'Reach', value: 342000, unit: '' },
    { id: 'cpm', label: 'CPM', value: 8.42, unit: '$' },
  ],
  verdictDistribution: { scale: 4, fix: 3, kill: 3 },
  weeklyDeltas: [
    { metric: 'scaleRate', label: 'Scale Rate', currentValue: '33%', previousValue: '30%', delta: '+3pp', direction: 'up', isPositive: true },
    { metric: 'vtr', label: 'VTR', currentValue: '28.4%', previousValue: '31.2%', delta: '-2.8pp', direction: 'down', isPositive: false },
    { metric: 'brandCueBy2s', label: 'Brand Cue by 2s', currentValue: '72%', previousValue: '68%', delta: '+4pp', direction: 'up', isPositive: true },
    { metric: 'avgEditCount', label: 'Avg Edits (Fix)', currentValue: '2.0', previousValue: '2.5', delta: '-0.5', direction: 'down', isPositive: true },
  ],

  topPerformers: [BB_CREATIVES[2]!, BB_CREATIVES[0]!, BB_CREATIVES[1]!],
  taxonomyInsights: [
    { field: 'Hook Type', option: 'Testimonial', creativeCount: 3, avgKpiValue: 34.1, portfolioAvgKpiValue: 28.4, multiplier: 1.2, kpiLabel: 'VTR' },
    { field: 'Format', option: 'UGC', creativeCount: 4, avgKpiValue: 33.5, portfolioAvgKpiValue: 28.4, multiplier: 1.18, kpiLabel: 'VTR' },
    { field: 'Hook Type', option: 'Static', creativeCount: 1, avgKpiValue: 7.3, portfolioAvgKpiValue: 28.4, multiplier: 0.26, kpiLabel: 'VTR' },
  ],
  allCreatives: BB_CREATIVES,

  gateFailures: [
    { gateName: 'Brand Timing (2s)', failCount: 5, failRate: 42, role: 'critical', topReason: 'First brand cue appears after 4 seconds on average in failing creatives' },
    { gateName: 'CTA Present', failCount: 4, failRate: 33, role: 'standard', topReason: 'No verbal or visual call-to-action in the ad' },
    { gateName: 'Clear Claim', failCount: 3, failRate: 25, role: 'standard', topReason: 'Multiple competing messages dilute the core claim' },
    { gateName: 'Brand Colors', failCount: 1, failRate: 8, role: 'standard', topReason: 'Brand palette inconsistently applied' },
  ],
  luckyHits: [BB_CREATIVES[9]!], // Lucky UGC Reel: Kill verdict but 72nd percentile performance
  underperformers: [BB_CREATIVES[5]!], // Influencer Collab: Scale verdict but only 55th percentile

  recommendations: [
    { type: 'quickWin', title: '3 Fix creatives need minor edits', description: 'Brand Film 60s, Product Close-up 6s, and Behind the Scenes each need 1-3 edits. Estimated effort: Small.', creativeIds: ['bb4', 'bb5', 'bb8'], impact: 'Could convert 3 Fix → Scale' },
    { type: 'kill', title: '2 creatives to stop spending on', description: 'Seasonal Promo 15s and Generic Banner v1 fail critical gates and perform in the bottom 20%. Total spend: $4,600/month.', creativeIds: ['bb7', 'bb9'], impact: 'Save $4,600/month' },
    { type: 'pattern', title: 'UGC + Testimonial hooks drive highest VTR', description: 'Your top 3 performers all use testimonial hooks in UGC format. Average VTR is 1.2x higher than portfolio average. Brief more creatives with this pattern.', creativeIds: ['bb3', 'bb6', 'bb10'], impact: 'Potential +20% portfolio VTR' },
  ],

  brandMetrics: {
    brandCueBy2sRate: 72,
    avgLogoDuration: 4.2,
    avgProductDuration: 6.8,
    brandLinkageDistribution: { high: 4, medium: 5, low: 3 },
  },

  qualityGates: [
    { gateId: 'gate_productionErrors', name: 'Production Quality', passRate: 92, passCount: 11, failCount: 1, role: 'critical' },
    { gateId: 'gate_formatHygiene', name: 'Format Hygiene', passRate: 83, passCount: 10, failCount: 2, role: 'critical' },
    { gateId: 'gate_brandTiming', name: 'Brand Timing (2s)', passRate: 58, passCount: 7, failCount: 5, role: 'critical' },
    { gateId: 'gate_clearClaim', name: 'Clear Claim', passRate: 75, passCount: 9, failCount: 3, role: 'standard' },
    { gateId: 'gate_productShown', name: 'Product Shown', passRate: 83, passCount: 10, failCount: 2, role: 'standard' },
    { gateId: 'gate_ctaPresent', name: 'CTA Present', passRate: 67, passCount: 8, failCount: 4, role: 'standard' },
    { gateId: 'gate_brandColorsUsed', name: 'Brand Colors', passRate: 92, passCount: 11, failCount: 1, role: 'standard' },
  ],
};

// --- Direct Response Dashboard Data ---

const DR_CREATIVES: DashboardCreative[] = [
  { id: 'dr1', name: 'UGC Hook Test A', verdict: 'Scale', qualityScore: 100, performanceScore: 91, primaryKpiLabel: 'ROAS', primaryKpiValue: '6.2x', spend: 18500, gatesPassed: 6, gatesTotal: 6, editCount: 0, taxonomyTags: { hookType: 'Question', format: 'UGC', cta: 'Shop Now' } },
  { id: 'dr2', name: 'Problem-Solution 15s', verdict: 'Scale', qualityScore: 83, performanceScore: 78, primaryKpiLabel: 'ROAS', primaryKpiValue: '4.8x', spend: 12000, gatesPassed: 5, gatesTotal: 6, editCount: 0, taxonomyTags: { hookType: 'Problem', format: 'Studio', cta: 'Get Offer' } },
  { id: 'dr3', name: 'Flash Sale Reel', verdict: 'Fix', qualityScore: 67, performanceScore: 65, primaryKpiLabel: 'ROAS', primaryKpiValue: '3.9x', spend: 8800, gatesPassed: 4, gatesTotal: 6, editCount: 2, taxonomyTags: { hookType: 'Offer', format: 'Motion Graphics', cta: 'Shop Now' } },
  { id: 'dr4', name: 'Founder Story 30s', verdict: 'Kill', qualityScore: 33, performanceScore: 25, primaryKpiLabel: 'ROAS', primaryKpiValue: '1.4x', spend: 5200, gatesPassed: 2, gatesTotal: 6, editCount: 0, taxonomyTags: { hookType: 'Story', format: 'Studio', cta: 'Learn More' } },
  { id: 'dr5', name: 'Comparison Ad', verdict: 'Scale', qualityScore: 100, performanceScore: 35, primaryKpiLabel: 'ROAS', primaryKpiValue: '2.1x', spend: 3400, gatesPassed: 6, gatesTotal: 6, editCount: 0, taxonomyTags: { hookType: 'Comparison', format: 'Studio', cta: 'Try Free' } },
  { id: 'dr6', name: 'Unboxing UGC', verdict: 'Kill', qualityScore: 17, performanceScore: 82, primaryKpiLabel: 'ROAS', primaryKpiValue: '5.5x', spend: 14200, gatesPassed: 1, gatesTotal: 6, editCount: 0, taxonomyTags: { hookType: 'Unboxing', format: 'UGC', cta: 'Link in Bio' } },
];

export const DASHBOARD_DIRECT_RESPONSE_V2: NarrativeDashboardData = {
  templateName: 'Direct Response',
  templateKey: 'directResponse',
  mode: 'in-market',
  totalCreatives: 6,
  analyzedCreatives: 6,
  withMetaData: 6,

  primaryKpi: { id: 'roas', label: 'ROAS', value: 3.8, unit: 'x' },
  secondaryKpis: [
    { id: 'hookRate', label: 'Hook Rate', value: 42.5, unit: '%' },
    { id: 'cpa', label: 'CPA', value: 18.40, unit: '$' },
  ],
  verdictDistribution: { scale: 3, fix: 1, kill: 2 },
  weeklyDeltas: [
    { metric: 'roas', label: 'ROAS', currentValue: '3.8x', previousValue: '3.2x', delta: '+0.6x', direction: 'up', isPositive: true },
    { metric: 'hookRate', label: 'Hook Rate', currentValue: '42.5%', previousValue: '38.1%', delta: '+4.4pp', direction: 'up', isPositive: true },
    { metric: 'cpa', label: 'CPA', currentValue: '$18.40', previousValue: '$21.60', delta: '-$3.20', direction: 'down', isPositive: true },
    { metric: 'scaleRate', label: 'Scale Rate', currentValue: '50%', previousValue: '33%', delta: '+17pp', direction: 'up', isPositive: true },
  ],

  topPerformers: [DR_CREATIVES[0]!, DR_CREATIVES[1]!],
  taxonomyInsights: [
    { field: 'Hook Type', option: 'Question', creativeCount: 1, avgKpiValue: 6.2, portfolioAvgKpiValue: 3.8, multiplier: 1.63, kpiLabel: 'ROAS' },
    { field: 'Format', option: 'UGC', creativeCount: 2, avgKpiValue: 5.85, portfolioAvgKpiValue: 3.8, multiplier: 1.54, kpiLabel: 'ROAS' },
    { field: 'CTA', option: 'Shop Now', creativeCount: 2, avgKpiValue: 5.05, portfolioAvgKpiValue: 3.8, multiplier: 1.33, kpiLabel: 'ROAS' },
    { field: 'Hook Type', option: 'Story', creativeCount: 1, avgKpiValue: 1.4, portfolioAvgKpiValue: 3.8, multiplier: 0.37, kpiLabel: 'ROAS' },
  ],
  allCreatives: DR_CREATIVES,

  gateFailures: [
    { gateName: 'Hook Strength', failCount: 3, failRate: 50, role: 'critical', topReason: 'Generic opening without pattern interrupt — no curiosity gap or visual contrast in first 3 seconds' },
    { gateName: 'Offer Clarity', failCount: 2, failRate: 33, role: 'critical', topReason: 'Value proposition is vague or missing — no specific offer, price, or benefit stated' },
    { gateName: 'CTA Prominence', failCount: 2, failRate: 33, role: 'critical', topReason: 'Weak CTA ("Learn More") or CTA appears too late in the video' },
  ],
  luckyHits: [DR_CREATIVES[5]!], // Unboxing UGC: Kill verdict but 82nd percentile ROAS
  underperformers: [DR_CREATIVES[4]!], // Comparison Ad: Scale verdict but only 35th percentile

  recommendations: [
    { type: 'quickWin', title: 'Flash Sale Reel needs 2 edits', description: 'Add urgency mechanic (countdown timer) and make CTA more prominent. Currently "Fix" with 3.9x ROAS — could become a top performer.', creativeIds: ['dr3'], impact: 'Could convert Fix → Scale with 2 small edits' },
    { type: 'kill', title: 'Founder Story 30s: stop spend', description: 'Fails Hook Strength and Offer Clarity (both critical). ROAS 1.4x is below breakeven. Spending $5,200/month.', creativeIds: ['dr4'], impact: 'Save $5,200/month' },
    { type: 'pattern', title: 'Question hooks + UGC format = highest ROAS', description: 'UGC Hook Test A uses a question hook in UGC format and leads at 6.2x ROAS. Brief more creatives with this combination.', creativeIds: ['dr1'], impact: 'Potential +63% ROAS vs portfolio average' },
    { type: 'pattern', title: 'Lucky Hit: Unboxing UGC performing despite quality issues', description: 'This creative has 5.5x ROAS but fails 5 of 6 gates. Something resonates with the audience — investigate what works and recreate with proper quality.', creativeIds: ['dr6'], impact: 'Could unlock high-performing creative pattern' },
  ],

  qualityGates: [
    { gateId: 'gate_productionErrors', name: 'Production Quality', passRate: 83, passCount: 5, failCount: 1, role: 'critical' },
    { gateId: 'gate_formatHygiene', name: 'Format Hygiene', passRate: 83, passCount: 5, failCount: 1, role: 'critical' },
    { gateId: 'gate_hookStrength', name: 'Hook Strength', passRate: 50, passCount: 3, failCount: 3, role: 'critical' },
    { gateId: 'gate_offerClarity', name: 'Offer Clarity', passRate: 67, passCount: 4, failCount: 2, role: 'critical' },
    { gateId: 'gate_ctaProminence', name: 'CTA Prominence', passRate: 67, passCount: 4, failCount: 2, role: 'critical' },
    { gateId: 'gate_urgency', name: 'Urgency / Scarcity', passRate: 33, passCount: 2, failCount: 4, role: 'standard' },
  ],
};

